async function isUKVisitor() {
  try {
    const res = await fetch("/browsing_context_suggestions.json");
    const data = await res.json();
    return data?.detected_values?.country?.handle === "GB";
  } catch (err) {
    console.error("Cannot detect UK visitor:", err);
    return false;
  }
}

class GiftWithPurchaseHandler extends HTMLElement {
  connectedCallback() {
    this.variantId = parseInt(this.dataset.variantId);
    this.threshold = parseInt(this.dataset.threshold || "25000");
    this.isProcessing = false;

    this.init();
  }

  async init() {
    this.isUK = await isUKVisitor();
    if (!this.isUK) {
      await this.removeGiftIfNonUK();
      return;
    }
    document.addEventListener("cart:updated", this.handleCartUpdate.bind(this));
    setTimeout(() => this.checkAndUpdateGift(), 300);
  }

  async removeGiftIfNonUK() {
    try {
      const cartData = await this.getCartData();

      if (!cartData) return;
      const giftInCart = this.findGiftInCart(cartData);
      if (giftInCart) {
        await this.removeGiftFromCart(giftInCart);
      }
    } catch (err) {
      console.error("Failed to remove gift for non-UK visitor:", err);
    }
  }
  async handleCartUpdate() {
    if (this.isProcessing) return;
    await this.checkAndUpdateGift();
  }

  async checkAndUpdateGift() {
    this.isProcessing = true;
    try {
      const cartData = await this.getCartData();
      if (!cartData) return;

      const isConditionMet = this.isConditionMet(cartData);
      const giftInCart = this.findGiftInCart(cartData);

      if (isConditionMet && !giftInCart) {
        await this.addGiftToCart();
      } else if (!isConditionMet && giftInCart) {
        await this.removeGiftFromCart(giftInCart);
      }
    } catch (e) {
      console.error("GWP error:", e);
    } finally {
      this.isProcessing = false;
    }
  }

  async getCartData() {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) throw new Error("Failed to fetch cart");
      return await res.json();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  isConditionMet(cartData) {
    if (!this.isUK) return false;
    const totalWithoutGift = cartData.items
      .filter((i) => i.variant_id !== this.variantId)
      .reduce((s, i) => s + i.line_price, 0);
    return totalWithoutGift >= this.threshold;
  }

  findGiftInCart(cartData) {
    return cartData.items.find((i) => i.variant_id === this.variantId && i.properties?._gift === "true");
  }

  async addGiftToCart() {
    const body = JSON.stringify({
      items: [
        {
          id: this.variantId,
          quantity: 1,
          properties: {
            _gift: "true",
            _free_gift: "Kick Game Dust Bag",
          },
        },
      ],
    });

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });

    if (!res.ok) throw new Error("Failed to add gift");
    this.refreshCartUI();
  }

  async removeGiftFromCart(giftItem) {
    const cartItemsEl = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
    if (cartItemsEl && typeof cartItemsEl.updateQuantity === "function") {
      const index = giftItem?.key ? await this.getGiftLineIndex(giftItem.key) : null;
      if (index !== null) {
        cartItemsEl.updateQuantity(index, 0);
      } else {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 }),
        });
        this.refreshCartUI();
      }
    }
  }

  async getGiftLineIndex(giftKey) {
    const cartData = await this.getCartData();
    const idx = cartData?.items?.findIndex((i) => i.key === giftKey);
    return idx >= 0 ? idx + 1 : null;
  }

  async refreshCartUI() {
    try {
      const cartItemsEl = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
      if (cartItemsEl && typeof cartItemsEl.onCartUpdate === "function") {
        await cartItemsEl.onCartUpdate();
      }
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.renderContents === "function") {
        const res = await fetch("/?sections=cart-drawer,cart-icon-bubble");
        const data = await res.json();
        cartDrawer.renderContents({
          id: "gift-refresh",
          sections: data,
        });
      }
      const cartData = await this.getCartData();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-with-purchase", cartData });
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cartData } }));
    } catch (err) {
      console.error("GWP: Failed to refresh UI", err);
    }
  }
}

if (!customElements.get("gift-with-purchase-handler")) {
  customElements.define("gift-with-purchase-handler", GiftWithPurchaseHandler);
}
